import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";

type ResolveRecord = Record<string, unknown>;

export interface ReservationMealPeriodPrintRow extends ResolveRecord {
    id?: string;
    start?: Date;
    reservation: ResolveRecord;
    restaurant?: ResolveRecord;
    resource?: ResolveRecord;
    sectorResources: ResolveRecord[];
}

const toDate = (value: unknown): Date | undefined => {
    if (!(value instanceof Date) && typeof value !== "string" && typeof value !== "number") return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

export const getLinkedReservations = async (reservation: Reservation): Promise<Reservation[]> => {
    if (!reservation.reservationGroupId) return [reservation];

    const byId = new Map<string, Reservation>();
    const pageSize = 100;

    for (let offset = 0; ; offset += pageSize) {
        // A failed page must fail the print load rather than produce a partial meal plan.
        const page = await ReservationService.getAllReservations({
            reservationGroupId: [reservation.reservationGroupId],
            archived: false,
            limit: {
                offset,
                limit: pageSize,
                sort: [
                    {field: "expectedStartDate", direction: "ASC"},
                    {field: "id", direction: "ASC"},
                ],
            },
        });
        const rows = page.content ?? [];
        rows.forEach(row => {
            if (row.id) byId.set(row.id, row);
        });

        if (page.last === true || !rows.length || (page.last !== false && rows.length < pageSize)) break;
    }

    if (reservation.id) byId.set(reservation.id, reservation);
    return Array.from(byId.values());
};

export const getReservationResources = (
    reservation: Reservation,
    resources: Resource[],
    sector?: Sector,
): {reservationResources: Resource[]; restaurantResource?: Resource} => {
    const assignedIds = new Set(reservation.resourceIds ?? []);
    const assigned = resources.filter(resource => resource.id && assignedIds.has(resource.id));
    if (sector?.sectorType !== SectorType.RESTAURANT) return {reservationResources: assigned};

    const parentIds = new Set(assigned.map(resource => resource.parentId).filter(Boolean));
    const restaurantResource = resources.find(resource => resource.id && parentIds.has(resource.id))
        ?? assigned.find(resource => !resource.parentId);

    return {
        restaurantResource,
        reservationResources: assigned.filter(resource => resource.id !== restaurantResource?.id),
    };
};

const MEAL_PLAN_STATUSES = new Set([
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
]);
const MEAL_PERIODS = new Set(["breakfast", "lunch", "dinner"]);

export const getReservationMealPeriodRows = (
    reservations: Reservation[],
    resources: Resource[],
    sector?: Sector,
): ReservationMealPeriodPrintRow[] => {
    if (sector?.sectorType !== SectorType.RESTAURANT) return [];

    return reservations
        .filter(reservation => (
            reservation.sectorId === sector.id
            && reservation.archived !== true
            && reservation.status !== undefined
            && MEAL_PLAN_STATUSES.has(reservation.status)
            && MEAL_PERIODS.has(String(reservation.meta?.eatPeriod ?? "").trim())
        ))
        .map(reservation => {
            const {reservationResources, restaurantResource} = getReservationResources(reservation, resources, sector);
            const sectorResources = reservationResources as unknown as ResolveRecord[];

            return {
                id: reservation.id,
                reservation: reservation as unknown as ResolveRecord,
                resource: sectorResources[0],
                restaurant: restaurantResource as unknown as ResolveRecord | undefined,
                sectorResources,
                start: toDate(reservation.expectedStartDate),
            };
        })
        .sort((left, right) => (
            (left.start?.getTime() ?? Infinity) - (right.start?.getTime() ?? Infinity)
            || (left.id ?? "").localeCompare(right.id ?? "")
        ));
};
